/*
 * founders-slider.js · Video-Slider „Von Experten erklärt“ / „Es beginnt in deinen Zellen“
 * Stand 21.10.2026: Autoplay ohne Ton, sobald das Video zur Hälfte im Bild ist, „Ton an“-Knopf
 * über dem Video, Player-Leiste mit Spulen, Ton-Wahl merken, Clips automatisch weiterschalten,
 * Play-Knopf als Ersatz, wenn der Browser Autoplay blockt, Messung an Clarity und dataLayer.
 * 28.10.2026: Standbilder und Clip 1 erst eine Bildschirmhöhe vor dem Slider laden,
 * nächsten Clip ab einem Viertel des laufenden vorladen.
 * 29.10.2026: „Ton an“ immer zeigen, solange das Video ohne Ton läuft, auch nach gemerktem „Ton aus“.
 */
const SN_TON_KEY = 'sn-video-ton'; // 'an' | 'aus', vom Besucher gewählt
const SN_ICON_TON_AUS = '<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M11 5 6 9H3v6h3l5 4V5z"/><path d="m16 9 5 6M21 9l-5 6"/></svg>';
const SN_ICON_PLAY = '<svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor" aria-hidden="true"><path d="M8 5.5v13a1 1 0 0 0 1.5.86l10.6-6.5a1 1 0 0 0 0-1.72L9.5 4.64A1 1 0 0 0 8 5.5z"/></svg>';
const SN_ICON_PAUSE = '<svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor" aria-hidden="true"><rect x="6" y="5" width="4" height="14" rx="1.5"/><rect x="14" y="5" width="4" height="14" rx="1.5"/></svg>';

function snLeseTon() {
  try { return localStorage.getItem(SN_TON_KEY); } catch (e) { return null; }
}

function snSpeicherTon(wert) {
  try { localStorage.setItem(SN_TON_KEY, wert); } catch (e) { /* privater Modus: nur für diese Seite */ }
}

function snZeit(sekunden) {
  const s = Math.max(0, Math.round(sekunden || 0));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

class FoundersSlider extends HTMLElement {
  connectedCallback() {
    this.slider = this.querySelector('[data-slider]');
    if (!this.slider) return;

    this.navItems = Array.from(this.querySelectorAll('[data-nav-item]'));
    this.navButtons = Array.from(this.querySelectorAll('[data-nav-button]'));
    this.slides = Array.from(this.querySelectorAll('[data-slide]'));
    this.videos = this.slides.map((slide) => slide.querySelector('[data-video]'));
    this.soundButton = this.querySelector('[data-sound-toggle]');
    this.mobileQuery = window.matchMedia('(max-width: 989px)');
    this.touchQuery = window.matchMedia('(hover: none)');
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.activeIndex = 0;
    this.tonWahl = snLeseTon();
    this.isMuted = this.tonWahl !== 'an';
    this.isVisible = false;
    this.userPaused = false;
    this.reloadingVideos = new Set();
    this.gemeldet = new Set();
    this.ui = [];

    this.eachVideo((video, index) => {
      video.playsInline = true;
      video.muted = true;
      video.addEventListener('play', () => this.onPlayStateChange());
      video.addEventListener('pause', () => this.onPlayStateChange());
      video.addEventListener('ended', () => this.onEnded(index));
      video.addEventListener('timeupdate', () => this.onTimeUpdate(index));
      video.addEventListener('loadedmetadata', () => this.updateBar(index));
      video.addEventListener('click', this.onVideoClick.bind(this));
      if (index === 0) this.applyVideoPoster(video); // übrige Standbilder erst, wenn der Slider näherkommt
      this.ui[index] = this.buildControls(this.slides[index], video, index);
    });

    this.navButtons.forEach((button) => button.addEventListener('click', this.onNavClick.bind(this)));
    this.soundButton?.addEventListener('click', this.onSoundClick.bind(this));
    this.mobileQuery.addEventListener('change', this.onBreakpointChange.bind(this));
    this.slider.addEventListener('swiper:init', this.onSwiperInit.bind(this));
    if (this.slider.swiper) this.onSwiperInit();

    this.visibilityObserver = new IntersectionObserver(this.onVisibilityChange.bind(this), { threshold: [0, 0.5] });
    this.visibilityObserver.observe(this.slider);

    // Eine Bildschirmhöhe vor dem Slider: übrige Standbilder setzen und den ersten Clip anladen
    this.naheObserver = new IntersectionObserver((entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) return;
      this.naheObserver.disconnect();
      this.eachVideo((video) => this.applyVideoPoster(video));
      this.vorladen(this.activeIndex);
    }, { rootMargin: '100% 0px' });
    this.naheObserver.observe(this.slider);

    this.markActiveSlide();
    this.syncMutedState();
    this.onPlayStateChange();
  }

  disconnectedCallback() {
    this.visibilityObserver?.disconnect();
    this.naheObserver?.disconnect();
  }

  get activeVideo() {
    return this.videos[this.activeIndex];
  }

  eachVideo(callback) {
    this.videos.forEach((video, index) => {
      if (video) callback(video, index);
    });
  }

  /* ---------- Bedienelemente über dem Video ---------- */

  buildControls(slide, video, index) {
    slide.classList.add('founders__slide--player');

    const unmute = document.createElement('button');
    unmute.type = 'button';
    unmute.className = 'founders__unmute';
    unmute.setAttribute('aria-label', 'Ton an'); // am Handy steht nur das Symbol
    unmute.innerHTML = `${SN_ICON_TON_AUS}<span>Ton an</span>`;
    unmute.hidden = true;
    unmute.addEventListener('click', () => this.tonAn());

    const bigPlay = document.createElement('button');
    bigPlay.type = 'button';
    bigPlay.className = 'founders__bigplay';
    bigPlay.setAttribute('aria-label', 'Video abspielen');
    bigPlay.innerHTML = SN_ICON_PLAY;
    bigPlay.hidden = true;
    bigPlay.addEventListener('click', () => this.playByUser(index));

    const bar = document.createElement('div');
    bar.className = 'founders__bar swiper-no-swiping';
    bar.innerHTML = `
      <button type="button" class="founders__bar-play" aria-label="Abspielen">${SN_ICON_PLAY}</button>
      <input type="range" class="founders__seek" min="0" max="1000" step="1" value="0" aria-label="Stelle im Video">
      <span class="founders__time">0:00</span>`;
    const playButton = bar.querySelector('.founders__bar-play');
    const seek = bar.querySelector('.founders__seek');
    const time = bar.querySelector('.founders__time');
    playButton.addEventListener('click', () => this.togglePlay(index));
    seek.addEventListener('input', () => {
      if (video.duration) video.currentTime = (seek.value / 1000) * video.duration;
      this.updateBar(index);
      this.showControls(index);
      this.meldeEinmal(`spulen-${index}`, 'video_spulen', index);
    });
    bar.addEventListener('pointerdown', () => this.showControls(index));
    // Maus bewegt sich übers Video: Leiste 3 s zeigen, danach wieder frei für die Untertitel
    slide.addEventListener('mousemove', () => {
      if (index === this.activeIndex && !this.touchQuery.matches) this.showControls(index);
    });

    const line = document.createElement('span');
    line.className = 'founders__line';
    line.setAttribute('aria-hidden', 'true');

    slide.append(unmute, bigPlay, bar, line);

    // Zähler oben rechts wie bei Instagram-Karussells, z. B. „1/7“
    if (this.slides.length > 1) {
      const zaehler = document.createElement('span');
      zaehler.className = 'founders__count';
      zaehler.textContent = `${index + 1}/${this.slides.length}`;
      zaehler.setAttribute('aria-hidden', 'true');
      slide.append(zaehler);
    }
    return { slide, unmute, bigPlay, playButton, seek, time, line, hideTimer: null };
  }

  updateBar(index) {
    const video = this.videos[index];
    const ui = this.ui[index];
    if (!video || !ui) return;
    const anteil = video.duration ? video.currentTime / video.duration : 0;
    ui.seek.value = Math.round(anteil * 1000);
    ui.seek.style.setProperty('--sn-progress', `${anteil * 100}%`);
    ui.line.style.setProperty('--sn-progress', anteil);
    ui.time.textContent = video.duration ? `-${snZeit(video.duration - video.currentTime)}` : '0:00';
  }

  // Leiste kurz einblenden (Handy: erster Tipp, während das Video läuft)
  showControls(index) {
    const ui = this.ui[index];
    if (!ui) return;
    ui.slide.classList.add('founders__slide--controls');
    clearTimeout(ui.hideTimer);
    ui.hideTimer = setTimeout(() => ui.slide.classList.remove('founders__slide--controls'), 3000);
  }

  syncOverlays() {
    this.eachVideo((video, index) => {
      const ui = this.ui[index];
      const isActive = index === this.activeIndex;
      const neuOderZuEnde = video.paused && (video.currentTime === 0 || video.ended);
      ui.unmute.hidden = !(isActive && !video.paused && video.muted); // auch nach gemerktem „Ton aus“
      ui.bigPlay.hidden = !(isActive && video.paused); // pausiert = Play-Knopf mittig
      ui.slide.classList.toggle('founders__slide--paused', video.paused);
      ui.slide.classList.toggle('founders__slide--neu', neuOderZuEnde); // Standbild: keine Leiste
      ui.playButton.innerHTML = video.paused ? SN_ICON_PLAY : SN_ICON_PAUSE;
      ui.playButton.setAttribute('aria-label', video.paused ? 'Abspielen' : 'Pause');
    });
  }

  /* ---------- Abspielen ---------- */

  autoplay() {
    const video = this.activeVideo;
    if (!video || !this.isVisible || this.userPaused || this.reducedMotion || !video.paused) return;
    this.ensureVideoSource(video);
    video.muted = this.isMuted;
    video.play()?.then(() => this.meldeEinmal('autoplay', 'video_autoplay', this.activeIndex)).catch(() => {
      // Browser erlaubt kein Autoplay mit Ton: ohne Ton versuchen, sonst bleibt der Play-Knopf stehen
      if (video.muted) return;
      this.isMuted = true;
      this.syncMutedState();
      video.play()?.catch(() => {});
    });
  }

  playByUser(index) {
    const video = this.videos[index];
    if (!video) return;
    if (index !== this.activeIndex) return this.goToSlide(index);
    this.userPaused = false;
    const neuerStart = video.currentTime === 0 || video.ended;
    if (neuerStart) {
      // Play-Knopf auf dem Standbild: mit Ton starten, außer der Besucher hat Ton aus gewählt
      video.currentTime = 0;
      this.isMuted = this.tonWahl === 'aus';
      this.syncMutedState();
      this.melde('video_play_klick', index);
    }
    this.ensureVideoSource(video);
    video.play()?.catch(() => {});
  }

  togglePlay(index) {
    const video = this.videos[index];
    if (!video) return;
    if (video.paused) return this.playByUser(index);
    this.userPaused = true;
    video.pause();
  }

  tonAn() {
    const video = this.activeVideo;
    if (!video) return;
    this.tonWahl = 'an';
    snSpeicherTon('an');
    this.isMuted = false;
    this.userPaused = false;
    this.syncMutedState();
    video.currentTime = 0; // Ton an = von vorn, sonst fehlt der Anfang
    video.play()?.catch(() => {});
    this.melde('video_ton_an', this.activeIndex);
  }

  onVideoClick(event) {
    const index = this.videos.indexOf(event.currentTarget);
    if (index !== this.activeIndex) return this.goToSlide(index);
    const video = this.activeVideo;
    if (!this.ui[index].unmute.hidden) return this.tonAn(); // stumm laufend: Tipp aufs Video = Ton an
    const leisteVersteckt = !this.ui[index].slide.classList.contains('founders__slide--controls');
    if (this.touchQuery.matches && !video.paused && leisteVersteckt) return this.showControls(index);
    this.togglePlay(index);
  }

  onPlayStateChange() {
    if (this.reloadingVideos.has(this.activeVideo)) return;
    const isPlaying = !!this.activeVideo && !this.activeVideo.paused;
    this.navItems.forEach((item, itemIndex) => {
      item.classList.toggle('founders__nav-item--playing', itemIndex === this.activeIndex && isPlaying);
    });
    this.navButtons[this.activeIndex]?.setAttribute('aria-pressed', isPlaying ? 'true' : 'false');
    this.soundButton?.setAttribute('aria-pressed', this.isMuted ? 'false' : 'true');
    this.syncOverlays();
  }

  onTimeUpdate(index) {
    this.updateBar(index);
    const video = this.videos[index];
    if (!video.duration) return;
    const prozent = (video.currentTime / video.duration) * 100;
    [25, 50, 75].forEach((marke) => {
      if (prozent >= marke) this.meldeEinmal(`${marke}-${index}`, `video_${marke}`, index);
    });
    // Ab einem Viertel des laufenden Clips den nächsten vorladen, damit er beim Weiterschalten sofort scharf startet
    if (prozent >= 25 && index === this.activeIndex) this.vorladen(index + 1);
  }

  vorladen(index) {
    const video = this.videos[index];
    if (!video || video.preload === 'auto') return;
    video.preload = 'auto';
    if (video.readyState > 0 || !video.paused) return; // lädt schon oder läuft: load() würde es neu starten
    if (!this.ensureVideoSource(video)) video.load();
  }

  onEnded(index) {
    this.meldeEinmal(`100-${index}`, 'video_100', index);
    const next = index + 1;
    if (index === this.activeIndex && next < this.videos.length && this.isVisible && !this.userPaused) {
      this.goToSlide(next);
      return;
    }
    this.onPlayStateChange();
  }

  onVisibilityChange(entries) {
    entries.forEach((entry) => {
      this.isVisible = entry.isIntersecting && entry.intersectionRatio >= 0.5;
      if (this.isVisible) {
        this.autoplay();
      } else {
        this.eachVideo((video) => video.pause());
      }
    });
  }

  /* ---------- Slider ---------- */

  goToSlide(index) {
    this.userPaused = false;
    if (this.slider.swiper) {
      this.slider.swiper.slideTo(index);
      return;
    }
    this.setActiveIndex(index);
  }

  onSwiperInit() {
    const { swiper } = this.slider;
    swiper.on('slideChange', () => this.setActiveIndex(swiper.activeIndex));
    this.setActiveIndex(swiper.activeIndex);
  }

  setActiveIndex(index) {
    if (index === this.activeIndex) return;
    this.eachVideo((video) => video.pause());
    this.activeIndex = index;
    this.userPaused = false;
    this.navItems.forEach((item, itemIndex) => {
      const isActive = itemIndex === index;
      const button = this.navButtons[itemIndex];
      item.classList.toggle('founders__nav-item--active', isActive);
      if (isActive) {
        button?.setAttribute('aria-current', 'true');
        return;
      }
      button?.removeAttribute('aria-current');
      button?.removeAttribute('aria-pressed');
    });
    this.markActiveSlide();
    this.syncMutedState();
    this.onPlayStateChange();
    this.autoplay();
  }

  markActiveSlide() {
    this.slides.forEach((slide, i) => slide.classList.toggle('founders__slide--active', i === this.activeIndex));
  }

  syncMutedState() {
    this.eachVideo((video, videoIndex) => {
      video.muted = this.isMuted || videoIndex !== this.activeIndex;
    });
    this.soundButton?.setAttribute('aria-pressed', this.isMuted ? 'false' : 'true');
    if (this.ui.length) this.syncOverlays();
  }

  onNavClick(event) {
    const index = Number(event.currentTarget.dataset.slideIndex);
    if (index === this.activeIndex) {
      this.togglePlay(index);
      return;
    }
    this.goToSlide(index);
  }

  onSoundClick() {
    if (this.isMuted && !this.ui[this.activeIndex].unmute.hidden) return this.tonAn();
    this.isMuted = !this.isMuted;
    this.tonWahl = this.isMuted ? 'aus' : 'an';
    snSpeicherTon(this.tonWahl);
    this.syncMutedState();
    this.melde(this.isMuted ? 'video_ton_aus' : 'video_ton_an', this.activeIndex);
  }

  /* ---------- Quellen Handy/Desktop (unverändert aus dem Theme) ---------- */

  applyVideoPoster(video) {
    const { posterDesktop, posterMobile } = video.dataset;
    const poster = (this.mobileQuery.matches && posterMobile) || posterDesktop || posterMobile;
    if (poster) video.poster = poster;
  }

  ensureVideoSource(video) {
    const mobileSources = Array.from(video.querySelectorAll('source[media]'));
    if (mobileSources.length === 0) return false;
    const wantsMobile = this.mobileQuery.matches;
    const isMobileSelected = mobileSources.some((source) => source.src === video.currentSrc);
    if (wantsMobile === isMobileSelected) return false;
    const desktopSources = Array.from(video.querySelectorAll('source:not([media])'));
    const [chosen] = wantsMobile ? mobileSources : desktopSources;
    if (!chosen) return false;
    video.setAttribute('src', chosen.src);
    video.load();
    return true;
  }

  reloadVideoSource(video, videoIndex) {
    const wasPlaying = !video.paused;
    const position = video.currentTime;
    this.reloadingVideos.add(video);
    if (!this.ensureVideoSource(video)) {
      this.reloadingVideos.delete(video);
      return;
    }
    video.muted = this.isMuted || videoIndex !== this.activeIndex;
    if (!wasPlaying) {
      this.reloadingVideos.delete(video);
      return;
    }
    video.addEventListener('loadedmetadata', () => {
      video.currentTime = Math.min(position, video.duration || position);
    }, { once: true });
    const playback = video.play();
    if (!playback) {
      this.reloadingVideos.delete(video);
      return;
    }
    playback.catch(() => {}).finally(() => {
      this.reloadingVideos.delete(video);
      this.onPlayStateChange();
    });
  }

  onBreakpointChange() {
    this.eachVideo((video, videoIndex) => {
      this.applyVideoPoster(video);
      this.reloadVideoSource(video, videoIndex);
    });
  }

  /* ---------- Messung: Clarity (Events + Tag) und dataLayer (GTM) ---------- */

  melde(aktion, index) {
    const clip = index + 1;
    const name = this.navButtons[index]?.getAttribute('aria-label')?.replace(/\s+/g, ' ').trim() || '';
    // Feste Kennung je Video aus der Shopify-Adresse (…/vp/<kennung>/…), bleibt gleich, auch wenn die Reihenfolge wechselt
    const quelle = this.videos[index]?.querySelector('source:not([media])')?.src || '';
    const videoId = (quelle.match(/\/vp\/([a-f0-9]+)\//) || [])[1] || '';
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: 'sn_video', video_aktion: aktion, video_clip: clip, video_name: name, video_id: videoId, video_ton: this.isMuted ? 'aus' : 'an', seite: location.pathname });
    if (typeof window.clarity === 'function') {
      window.clarity('event', aktion); // Summe über alle Clips
      window.clarity('event', `${aktion}_clip${clip}`); // je Clip, Seite über den URL-Filter in Clarity
      if (aktion === 'video_ton_an') window.clarity('set', 'video_ton', 'an');
    }
  }

  meldeEinmal(schluessel, aktion, index) {
    if (this.gemeldet.has(schluessel)) return;
    this.gemeldet.add(schluessel);
    this.melde(aktion, index);
  }
}

customElements.define('founders-slider', FoundersSlider);